import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'fill-[#FF2E2E] text-[#FF2E2E]' : 'text-white/20'}`}
        aria-hidden="true"
      />
    ));
  };

  // Calculate average rating for schema
  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1) 
    : "5.0";

  return (
    <section 
      id="reviews" 
      className="py-24 bg-[#050505] relative" 
      data-testid="reviews-section"
      aria-labelledby="reviews-heading"
      itemScope 
      itemType="https://schema.org/LocalBusiness"
    >
      <meta itemProp="name" content="Perry D Tires" />
      <div itemProp="aggregateRating" itemScope itemType="https://schema.org/AggregateRating">
        <meta itemProp="ratingValue" content={avgRating} />
        <meta itemProp="reviewCount" content={String(reviews.length || 6)} />
        <meta itemProp="bestRating" content="5" />
        <meta itemProp="worstRating" content="1" />
      </div>
      
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FF2E2E]/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 
            id="reviews-heading"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-4" 
            data-testid="reviews-title"
          >
            Client Reviews
          </h2>
          <p className="font-body text-white/60 text-lg max-w-2xl mx-auto">
            What our customers say about our service
          </p>
          <div className="w-24 h-1 bg-[#FF2E2E] mx-auto mt-6" aria-hidden="true" />
        </motion.div>

        {/* Reviews Grid */}
        {loading ? (
          <div className="text-center text-white/60 py-20" role="status" aria-live="polite">Loading reviews...</div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            role="list"
            aria-label="Customer reviews"
          >
            {reviews.map((review, index) => (
              <motion.article
                key={review.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass border-glow p-8 relative"
                data-testid={`review-card-${index}`}
                itemScope
                itemType="https://schema.org/Review"
                role="listitem"
              >
                <meta itemProp="itemReviewed" content="Perry D Tires" />
                
                {/* Quote Icon */}
                <Quote className="absolute top-6 right-6 w-8 h-8 text-[#FF2E2E]/20" aria-hidden="true" />
                
                {/* Stars */}
                <div 
                  className="flex gap-1 mb-4" 
                  role="img" 
                  aria-label={`Rating: ${review.rating} out of 5 stars`}
                  itemProp="reviewRating" 
                  itemScope 
                  itemType="https://schema.org/Rating"
                >
                  <meta itemProp="ratingValue" content={String(review.rating)} />
                  <meta itemProp="bestRating" content="5" />
                  {renderStars(review.rating)}
                </div>
                
                {/* Comment */}
                <p className="font-body text-white/70 leading-relaxed mb-6" itemProp="reviewBody">
                  "{review.comment}"
                </p>
                
                {/* Author */}
                <div className="flex items-center gap-3" itemProp="author" itemScope itemType="https://schema.org/Person">
                  <div className="w-10 h-10 bg-[#FF2E2E]/20 border border-[#FF2E2E]/30 flex items-center justify-center" aria-hidden="true">
                    <span className="font-heading text-[#FF2E2E]">
                      {review.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-heading text-white" itemProp="name">{review.name}</p>
                    <p className="font-body text-white/40 text-sm">Verified Customer</p>
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Reviews;
